import { Field, FieldProps, Form, Formik } from 'formik';
import { logger } from '../../utils/logging';
import { NewUserFormInput } from '../../interfaces/interfaces';
import { useMutation } from '@apollo/client';
import {
  CreateInviteRequestDocument,
  SendAdminApprovalDocument,
} from '../../queries';
import { Col, Row, Form as BForm, Button } from 'react-bootstrap';
import TextField from '../text-field/text-field';
import { DropdownV2, OptionV2 } from '@spins-gcp/shared-components-react';
import styles from './new-user.module.scss';
import { useAtomValue, useSetAtom } from 'jotai';
import { companyIdAtom, userRoleAtom } from '../../state/atoms';
import { useState } from 'react';
import { NewUserWithAdminSubmitted } from './new-user-with-admin-submitted';
import { FormErrorPanel } from '../form-error-panel/form-error-panel';
import { NEW_USER_REQUEST_ERROR_MESSAGE } from '../../utils/constants';

interface NewUserWithAdminProps {
  roleOptions: OptionV2<string>[];
  initialValues: NewUserFormInput;
}

export const NewUserWithAdmin = ({
  roleOptions,
  initialValues,
}: NewUserWithAdminProps) => {
  const [createInviteRequest] = useMutation(CreateInviteRequestDocument);
  const [sendAdminApproval] = useMutation(SendAdminApprovalDocument);
  const setSelectedUserRole = useSetAtom(userRoleAtom);
  const companyId = useAtomValue(companyIdAtom);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async (values: NewUserFormInput) => {
    setError(undefined);

    if (!companyId) {
      setError(NEW_USER_REQUEST_ERROR_MESSAGE);
      return;
    }

    try {
      const { data: inviteData } = await createInviteRequest({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.workEmail,
          role: values.role,
        },
      });
      if (!inviteData?.createInviteRequest) {
        setError(NEW_USER_REQUEST_ERROR_MESSAGE);
        return;
      }

      const { data: approvalData } = await sendAdminApproval({
        variables: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.workEmail,
          role: values.role,
          companyId,
        },
      });
      if (!approvalData?.requestCompanyAdminApproval) {
        setError(NEW_USER_REQUEST_ERROR_MESSAGE);
        return;
      }

      setIsSubmitted(true);
    } catch (err) {
      logger.error(err);
      setError(NEW_USER_REQUEST_ERROR_MESSAGE);
    }
  };

  if (isSubmitted) return <NewUserWithAdminSubmitted />;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, values }) => {
        const allFieldsFilled = Object.values(values).every(
          (value) => value.trim() !== ''
        );

        return (
          <Form>
            <Row>
              <Col>
                <p className={'header'}>Ask your administrator for an invite</p>
                <p className={'subtext'}>
                  We found that the company you&apos;re a part of has an active
                  administrator. We require users to be invited by an
                  administrator to continue
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  label="First Name*"
                  name="firstName"
                  onKeyUp={() => undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  label="Last Name*"
                  name="lastName"
                  autoFocus={false}
                  onKeyUp={() => undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  label="Work Email"
                  name="workEmail"
                  disabled
                  autoFocus={false}
                  onKeyUp={() => undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BForm.Group controlId="role">
                  <BForm.Label column={'sm'} className={styles['label']}>
                    Role
                  </BForm.Label>
                  <Field name="role">
                    {({ field, form }: FieldProps<string>) => (
                      <DropdownV2.Dropdown
                        id="role"
                        name="role"
                        options={roleOptions}
                        value={field.value}
                        onChange={(value) => {
                          form.setFieldValue('role', value);
                          setSelectedUserRole(value);
                        }}
                        onBlur={() => form.setFieldTouched('role', true)}
                        placeholderText="Select a role"
                      />
                    )}
                  </Field>
                </BForm.Group>
              </Col>
            </Row>
            <FormErrorPanel message={error} />
            <Row className={'form-button-row'}>
              <Col>
                <Button
                  type={'submit'}
                  disabled={isSubmitting || !allFieldsFilled}
                  color="primary"
                  style={{ width: '100%' }}
                >
                  Request to join
                </Button>
              </Col>
            </Row>
            <Row className={'pt-2'}>
              <Col className={styles['sign-in-row']}>
                Having issues?{' '}
                <a href={`/authorize`}>Contact Customer Support</a>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};
